import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { initTheme } from './hooks/useTheme';
import './App.css';

// 导入页面组件
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import { AdminHome, DoctorHome, ModuleSelector } from './pages/Dashboard';
import ReportList from './pages/ReportList';
import ReportReview from './pages/ReportReview';
import ReportView from './pages/ReportView';
import AppointmentOrdersQueue from './pages/AppointmentOrdersQueue';
import PatientList from './pages/PatientList';
import PatientDetail from './pages/PatientDetail';
import ClinicReportView from './pages/ClinicReportView';
import ReviewedReportsList from './pages/ReviewedReportsList';
import DoctorList from './pages/DoctorList';
import UserCenter from './pages/UserCenter';
import AdminDebugDemo from './pages/AdminDebugDemo';
import AdminPatientManagement from './pages/AdminPatientManagement';
import HealthHouseStations from './pages/HealthHouseStations';
import HealthHouseMockConfig from './pages/HealthHouseMockConfig';
import HealthHouseConnectors from './pages/HealthHouseConnectors';
import ClinicManagement from './pages/ClinicManagement';
import TokenUsageDashboard from './pages/TokenUsageDashboard';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import { useAdminAuth } from './utils/adminAuth';
import { getActiveWorkspace } from './utils/workspace';
import { getLocaleDirection, normalizeUiLocale } from './utils/locale';

initTheme();
const RealtimeConsultations = React.lazy(() => import('./pages/RealtimeConsultations'));

// 动态标题更新组件
const TitleUpdater = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const getPageTitle = () => {
      const path = location.pathname;
      let pageTitle = t('dashboard.title');
      
      if (path === '/') {
        pageTitle = t('dashboard.title');
      } else if (path === '/doctor') {
        pageTitle = t('dashboard.doctorHome');
      } else if (path === '/admin') {
        pageTitle = t('dashboard.adminHome');
      } else if (path === '/login') {
        pageTitle = t('auth.loginTitle');
      } else if (path === '/admin-login') {
        pageTitle = t('dashboard.adminLogin');
      } else if (path.startsWith('/reviewed-reports')) {
        pageTitle = t('reports.reviewedReports');
      } else if (path.startsWith('/reports') && path.includes('/review')) {
        pageTitle = t('reports.reviewTitle');
      } else if (path.startsWith('/reports') && path.includes('/view')) {
        pageTitle = t('reports.viewReport');
      } else if (path.startsWith('/reports')) {
        pageTitle = t('reports.pendingReports');
      } else if (path.startsWith('/appointment-orders')) {
        pageTitle = t('dashboard.appointmentOrders');
      } else if (path.startsWith('/realtime-consultations')) {
        pageTitle = t('realtimeConsultations.title');
      } else if (path.startsWith('/admin/patients')) {
        pageTitle = t('dashboard.patientManagement');
      } else if (path.startsWith('/admin/token-usage')) {
        pageTitle = t('tokenUsage.title');
      } else if (path.startsWith('/patients')) {
        pageTitle = t('patients.listTitle');
      } else if (path.startsWith('/doctors')) {
        pageTitle = t('dashboard.doctorManagement');
      } else if (path.startsWith('/clinics')) {
        pageTitle = t('clinics.title');
      } else if (path.startsWith('/health-house-stations')) {
        pageTitle = t('healthHouseStations.title');
      } else if (path.startsWith('/health-house-connectors')) {
        pageTitle = t('healthHouseConnectors.title');
      } else if (path.startsWith('/clinic/reports')) {
        pageTitle = t('reports.clinicReportView');
      } else if (path.startsWith('/user-center')) {
        pageTitle = t('dashboard.userCenter');
      } else if (path.startsWith('/admin/debug-demo')) {
        pageTitle = t('debugDemo.title');
      }
      
      const titleSuffix = path === '/login' || getActiveWorkspace(path) === 'doctor'
        ? t('dashboard.doctorDesk')
        : t('dashboard.title');

      document.title = pageTitle === titleSuffix ? titleSuffix : `${pageTitle} - ${titleSuffix}`;
      const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
      document.documentElement.lang = locale;
      document.documentElement.dir = getLocaleDirection(locale);
      const description = document.querySelector('meta[name="description"]');
      if (description) {
        description.setAttribute('content', t('dashboard.metaDescription'));
      }
    };

    getPageTitle();
  }, [location.pathname, t, i18n.language, i18n.resolvedLanguage]);

  return null;
};

const AdminRoute = ({ children }) => {
  const { isAdminAuthenticated, loading } = useAdminAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  if (!isAdminAuthenticated) {
    return <Navigate to="/admin-login" replace state={{ from: location }} />;
  }

  return children;
};

function App() {
  return (
    <Router basename="/web">
      <Layout>
        <div className="App">
          <TitleUpdater />
          <Routes>
            {/* 公开路由 */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/" element={<ModuleSelector />} />
            <Route path="/doctor" element={
              <PrivateRoute>
                <DoctorHome />
              </PrivateRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminHome />
              </AdminRoute>
            } />
            
            {/* 需要医生权限的路由 */}
            <Route path="/realtime-consultations" element={
              <PrivateRoute>
                <React.Suspense fallback={<div aria-busy="true" />}>
                  <RealtimeConsultations />
                </React.Suspense>
              </PrivateRoute>
            } />
            <Route path="/reports" element={
              <PrivateRoute>
                <ReportList />
              </PrivateRoute>
            } />
            <Route path="/reviewed-reports" element={
              <PrivateRoute>
                <ReviewedReportsList />
              </PrivateRoute>
            } />
            <Route path="/appointment-orders" element={
              <PrivateRoute>
                <AppointmentOrdersQueue />
              </PrivateRoute>
            } />
            <Route path="/patients" element={
              <PrivateRoute>
                <PatientList />
              </PrivateRoute>
            } />
            <Route path="/patients/:id" element={
              <PrivateRoute>
                <PatientDetail />
              </PrivateRoute>
            } />
            <Route path="/reports/:id/review" element={
              <PrivateRoute>
                <ReportReview />
              </PrivateRoute>
            } />
            <Route path="/reports/:id/view" element={
              <PrivateRoute>
                <ReportView />
              </PrivateRoute>
            } />
            <Route path="/user-center" element={
              <PrivateRoute>
                <UserCenter />
              </PrivateRoute>
            } />
            <Route path="/doctors" element={
              <AdminRoute>
                <DoctorList />
              </AdminRoute>
            } />
            <Route path="/clinics" element={
              <AdminRoute>
                <ClinicManagement />
              </AdminRoute>
            } />
            <Route path="/health-house-stations" element={
              <AdminRoute>
                <HealthHouseStations />
              </AdminRoute>
            } />
            <Route path="/health-house-stations/:stationId/mock-config" element={
              <AdminRoute>
                <HealthHouseMockConfig />
              </AdminRoute>
            } />
            <Route path="/health-house-connectors" element={
              <AdminRoute>
                <HealthHouseConnectors />
              </AdminRoute>
            } />
            <Route path="/admin/debug-demo" element={
              <AdminRoute>
                <AdminDebugDemo />
              </AdminRoute>
            } />
            <Route path="/admin/patients" element={
              <AdminRoute>
                <AdminPatientManagement />
              </AdminRoute>
            } />
            <Route path="/admin/token-usage" element={
              <AdminRoute>
                <TokenUsageDashboard />
              </AdminRoute>
            } />

            {/* 诊所通过token查看报告 (公开路由，使用token参数进行验证) */}
            <Route path="/clinic/reports/:id" element={<ClinicReportView />} />
            
            {/* 其他未知路由重定向到根路由 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Layout>
    </Router>
  );
}

export default App; 
